import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, HelpCircle, Truck, ToolCase } from 'lucide-react';
import { Helmet } from 'react-helmet-async'; // Added for SEO
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import Nav from "../Header/Nav";
import { Link } from 'react-router-dom';

const FaqItem = ({ question, answer, isOpen, toggle, icon: Icon }) => {
  return (
    <div className={`border-b border-gray-100 transition-all duration-300 ${isOpen ? 'bg-gray-50/50' : ''}`}>
      <button
        onClick={toggle}
        className="w-full py-6 flex items-center justify-between text-left focus:outline-none"
      >
        <div className="flex items-center gap-4">
          <div className={`p-2 rounded-xl transition-colors ${isOpen ? 'bg-[#ffaa00] text-white' : 'bg-gray-100 text-[#8A8A89]'}`}>
            <Icon size={20} />
          </div>
          {/* Using h3 for semantic structure within the accordion */}
          <h3 className="font-black uppercase tracking-wider text-sm md:text-base">
            {question}
          </h3>
        </div>
        <div className="ml-4">
          {isOpen ? <Minus size={20} className="text-[#ffaa00]" /> : <Plus size={20} className="text-gray-400" />}
        </div>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="pb-8 pl-14 pr-4 text-gray-600 font-medium leading-relaxed">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Faq = () => {
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const faqData = [
    {
      category: "Livraison",
      icon: Truck,
      question: "Quels sont les délais de livraison au Maroc ?",
      answer: "Nous livrons en 24h sur l'axe Casablanca-Rabat-Kénitra. Pour les autres villes du Royaume, le délai est généralement de 48h à 72h ouvrables."
    },
    {
      category: "SAV",
      icon: ToolCase,
      question: "Quelle est la durée de la garantie sur vos produits ?",
      answer: "Tous nos équipements bénéficient d'une garantie constructeur de 12 à 24 mois. En cas de panne, notre service technique basé à Salé prend en charge la réparation ou le remplacement."
    },
    {
      category: "Paiement",
      icon: HelpCircle,
      question: "Proposez-vous le paiement à la livraison ?",
      answer: "Oui, nous acceptons le paiement en espèces à la livraison partout au Maroc pour plus de sécurité. Nous acceptons également les virements bancaires."
    }
  ];

  return (
    <>
      {/* --- SEO START --- */}
      <Helmet>
        <title>FAQ | Aide et Support Electro Titan Maroc</title>
        <meta key="description" name="description" content="Des questions sur la livraison à Salé, le paiement à la livraison au Maroc ou nos garanties SAV ? Retrouvez toutes les réponses de l'équipe Electro Titan." />
        <link rel="canonical" href={window.location.href} />
        
        {/* FAQPage Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqData.map(item => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
              }
            }))
          })}
        </script>
      </Helmet>
      {/* --- SEO END --- */}

      <Header />
      <Nav />

      <div className="min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-16 text-center"
          >
            <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter">
              Questions <span className="text-[#ffaa00]">Fréquentes</span>
            </h1>
            <div className="h-2 w-24 bg-[#ffaa00] mx-auto mt-4"></div>
            <p className="text-[#8A8A89] font-bold mt-6 uppercase text-xs tracking-[0.3em]">
              Besoin d'aide ? Trouvez vos réponses ici.
            </p>
          </motion.div>

          <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden px-6 md:px-10 py-4">
            {faqData.map((item, index) => (
              <FaqItem
                key={index}
                {...item}
                isOpen={openIndex === index}
                toggle={() => setOpenIndex(openIndex === index ? -1 : index)}
              />
            ))}
          </div>

          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="mt-16 text-center p-10 bg-gray-50 rounded-[2.5rem] border-2 border-dashed border-gray-200"
          >
            <h2 className="font-black uppercase tracking-widest text-lg mb-2">Pas de réponse à votre question ?</h2>
            <p className="text-[#8A8A89] font-bold mb-6">Notre équipe technique est disponible 6j/7.</p>
            <Link to={"/contact"}>
              <button className="bg-[#ffaa00] text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:shadow-lg transition-all active:scale-95">
                Contacter le Support
              </button>
            </Link>
          </motion.div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Faq;